import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';
import { getVideoPreviewUrl, type Post } from '../../utils/communityUtils';

type Props = {
  post: Post;
  onClose: () => void;
};

/**
 * Accessible, in-platform viewer for the ▶ Video chip in Community.
 * Only video attachments enter this player; multiple clips can be browsed.
 */
export default function CommunityPostVideoViewer({ post, onClose }: Props) {
  const clips = (post.media ?? []).filter(item => item.media_type === 'video' && Boolean(item.media_url));
  const [active, setActive] = useState(0);
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], video[controls]'
        ),
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, [onClose]);

  useEffect(() => {
    const player = videoRef.current;
    if (!player) return;
    // Opening the video is a user action, so attempt playback immediately.
    // Browser audio restrictions may require a second tap on native Play.
    void player.play().catch(() => { /* Native player controls remain available. */ });
  }, [active, post.post_id]);

  if (!clips.length) return null;
  const item = clips[Math.min(active, clips.length - 1)];

  return (
    <div className="v5-video-viewer-backdrop" role="presentation"
      onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
      <section ref={dialogRef} className="v5-video-viewer" role="dialog"
        aria-modal="true" aria-label="Community video player">
        <div className="v5-video-viewer-head">
          <strong>▶ Video{clips.length > 1 ? ' ' + (active + 1) + ' of ' + clips.length : ''}</strong>
          <button ref={closeRef} type="button" aria-label="Close video player" onClick={onClose}>
            <FiX size={19} aria-hidden="true" /> Close
          </button>
        </div>
        <div className="v5-video-viewer-stage">
          <video key={item.media_id || item.media_url} ref={videoRef} controls playsInline autoPlay
            preload="metadata" src={getVideoPreviewUrl(item.media_url)}
            aria-label={post.title || 'Grocery Community video'} />
        </div>
        <div className="v5-video-viewer-footer">
          <p>{post.title || post.content.slice(0, 120)}</p>
          <div className="v5-video-viewer-actions">
            {clips.length > 1 && (
              <div className="v5-video-viewer-pages" role="group" aria-label="Choose video">
                <button type="button" disabled={active === 0} onClick={() => setActive(i => i - 1)}>
                  <FiChevronLeft size={16} aria-hidden="true" /> Previous
                </button>
                <button type="button" disabled={active === clips.length - 1}
                  onClick={() => setActive(i => i + 1)}>
                  Next <FiChevronRight size={16} aria-hidden="true" />
                </button>
              </div>
            )}
            <Link href={'/community/post/' + encodeURIComponent(post.post_id)}
              onClick={onClose}>View conversation</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
